import { z } from 'zod';
import {
  DecimalString,
  IsoDate,
  IsoDateTime,
  PositiveDecimalString,
  UuidString,
} from '../../primitives.js';
import { TaskLabelRef } from './label.js';
import { ProjectVisibility } from './project.js';
import { StateGroup } from './state.js';
import { TaskProjectMembership } from './task-project.js';

/**
 * Task — главная сущность task-module. Универсальная задача с polymorphic-привязкой
 * к ERP-сущности (subject_type+subject_id) + secondary refs[].
 *
 * Ключевые свойства:
 *   - sequence_id   — PROJ-NNN, per-project advisory_lock pattern (см. Plane issue.py:189)
 *   - sort_order    — fractional float (шаг +10000) для drag-drop без переиндексации
 *   - subject_*     — primary привязка 1:1 (work_order / anomaly / approval / etc.)
 *   - refs[]        — secondary привязки 1:N (через through-таблицу task_refs)
 *   - custom_fields — JSONB, runtime-валидация через FieldDefinition[] проекта
 *   - assignees     — discriminated union (user / role / group + claim для role/group)
 *
 * См. `docs/STAITER_UX.md` §11.2 + §11.8 и `docs/backlogs/task-module/be-backlog.md` BE-S12.1-2.
 */

// ─── Enums ──────────────────────────────────────────────────────────

export const TaskPriority = z.enum(['urgent', 'high', 'medium', 'low', 'none']);
export type TaskPriority = z.infer<typeof TaskPriority>;

/** S21.2 — папка (контейнер) vs обычная задача. Папка группирует задачи через
 *  parent_task_id, без собственных дат — сводный бар считается из детей. */
export const TaskKind = z.enum(['task', 'folder']);
export type TaskKind = z.infer<typeof TaskKind>;

/** Типы ERP-сущностей, к которым может быть привязана задача (см. §11.8 UX). */
export const SubjectType = z.enum([
  'work_order',
  'job_card',
  'anomaly',
  'source_conflict',
  'approval',
  'stock_entry',
  'stock_reconciliation',
  'invoice_ar',
  'invoice_ap',
  'payment',
  'counterparty',
  'item',
  'batch',
  'period_close',
  'esf_filing',
  'kpn_filing',
  'integration',
]);
export type SubjectType = z.infer<typeof SubjectType>;

export const EstimateUom = z.enum(['hours', 'story_points', 'days']);
export type EstimateUom = z.infer<typeof EstimateUom>;

// ─── Assignee (discriminated union) ──────────────────────────────────

/**
 * User-assignee — конкретный человек. Самый частый случай.
 */
export const TaskAssigneeUser = z.object({
  type: z.literal('user'),
  /** Row id назначения (для claim/unclaim API). */
  id: UuidString.optional(),
  user_id: UuidString,
});

/**
 * Role-assignee — «любой ревьювер» / «любой бухгалтер». Claim-pattern:
 * первый юзер с этой ролью, нажавший «Взять в работу» — становится `claimed_by_user_id`.
 */
export const TaskAssigneeRole = z.object({
  type: z.literal('role'),
  id: UuidString.optional(),
  role_key: z.string().min(1).max(64),
  claimed_by_user_id: UuidString.nullable(),
});

/**
 * Group-assignee — назначение на группу (custom workspace group).
 */
export const TaskAssigneeGroup = z.object({
  type: z.literal('group'),
  id: UuidString.optional(),
  group_id: UuidString,
  claimed_by_user_id: UuidString.nullable(),
});

export const TaskAssignee = z.discriminatedUnion('type', [
  TaskAssigneeUser,
  TaskAssigneeRole,
  TaskAssigneeGroup,
]);
export type TaskAssignee = z.infer<typeof TaskAssignee>;

// ─── Task ────────────────────────────────────────────────────────────

/**
 * Поля задачи БЕЗ перекрёстных проверок.
 *
 * Вынесено ради `TaskListItem` (work-tasks-performance B8): списочная схема
 * повторяет те же поля, но без четырёх `.refine()` — они выполняются на КАЖДОЙ
 * из 500–2000 строк выдачи, а проверяют то, что уже гарантировано сервером при
 * записи. Копировать список полей второй раз нельзя: две копии разъедутся на
 * первом же новом поле.
 */
export const TaskShape = z.object({
  id: UuidString,
  entity_id: UuidString,
  /** Primary-проект (денорм is_primary junction-строки). Канонический
   *  `<код project>-<sequence_id>` считается от него. M2M-членство — в `memberships`. */
  project_id: UuidString,
  /** BB1 — все проекты задачи (включая primary). `.default([])` — forward-compat
   *  FE-parse до деплоя бэкенда (пустой массив = ещё нет junction-bundle). */
  project_ids: z.array(UuidString).default([]),
  /** BB1 — полное членство (per-project is_primary/sort_order/parent). */
  memberships: z.array(TaskProjectMembership).default([]),
  /** Sub-task (родитель в primary-проекте; per-project родитель — в memberships). */
  parent_task_id: UuidString.nullable(),
  /** S21.2 — 'task' | 'folder'. Папка — контейнер без собственных дат. */
  kind: TaskKind,
  state_id: UuidString,
  /** Уникальный per-project номер вида PROJ-NNN. Генерится через pg_advisory_xact_lock. */
  sequence_id: z.number().int().positive(),
  /** Fractional float для drag-drop. Default +65535, шаг +10000 для append. */
  sort_order: z.number().finite(),
  title: z.string().min(1).max(500),
  /** TipTap JSON description — pass-through (валидация на уровне editor). */
  description_json: z.unknown(),
  /** Plain-text description для search + email + i18n-renderers. */
  description_text: z.string(),
  priority: TaskPriority,
  start_date: IsoDate.nullable(),
  due_date: IsoDate.nullable(),
  /** Decimal-string (часы / story-points / дни — зависит от estimate_uom). */
  estimate_value: PositiveDecimalString.nullable(),
  estimate_uom: EstimateUom.nullable(),
  /**
   * B1 (project-task-progress §1.1) — собственный прогресс задачи, 0..100.
   *
   * **Сырое поле, не для показа напрямую.** Прогресс ≠ статус (И-1): завершённая
   * задача отображается как 100 независимо от значения, отменённая исключается из
   * агрегатов. Единственный корректный способ прочитать значение для UI/агрегата —
   * `displayProgress()` из `@staiter/shared/engines-pure/task-progress`.
   *
   * `.default('0')` — forward-compat: FE-parse не падает на payload бэкенда,
   * задеплоенного до этой миграции (паттерн `labels` / `project_ids`).
   */
  progress: DecimalString.default('0'),
  assignees: z.array(TaskAssignee),
  /** BE-5 — denorm метки (label-dots без N+1). `.default([])` — merge-gate:
        FE-parse не падает на payload до деплоя бэкенда. */
  labels: z.array(TaskLabelRef).default([]),
  /**
   * WTP-B3 релиз 2 (F97) — метки строкой идут идентификаторами; объекты собирает парсер ответа
   * фронта из `label_ids` + `label_directory` выдачи (`lib/tasks/api/label-directory.ts`).
   * `.optional()` — ответы без поля (старый бэкенд) разбираются по `labels`.
   */
  label_ids: z.array(UuidString).optional(),
  author_user_id: UuidString,
  /** Polymorphic primary subject (1:1, оба поля либо null, либо заданы). */
  subject_type: SubjectType.nullable(),
  subject_id: UuidString.nullable(),
  /** Custom fields (validated runtime через FieldDefinition[] проекта). */
  custom_fields: z.record(z.string(), z.unknown()),
  is_draft: z.boolean(),
  started_at: IsoDateTime.nullable(),
  /** Auto-fill при state.group=completed (см. engine state-machine BE-S12.1-8). */
  completed_at: IsoDateTime.nullable(),
  /** Soft-delete (30d retention). */
  archived_at: IsoDateTime.nullable(),
  /** Для задач из AutoTaskRule (anomaly/conflict/approval-driven). */
  auto_source_event_id: UuidString.nullable(),
  auto_rule_id: UuidString.nullable(),
  /** Для instance'ов RecurringRule. */
  recurring_rule_id: UuidString.nullable(),
  /** Idempotency key для RecurringRule — обычно YYYY-MM-DD. */
  recurring_period_key: z.string().min(1).max(64).nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type TaskShape = z.infer<typeof TaskShape>;

export const Task = TaskShape.refine((d) => (d.subject_type === null) === (d.subject_id === null), {
  message: 'subject_type и subject_id должны быть оба null или оба заданы',
  path: ['subject_id'],
})
  .refine((d) => (d.estimate_value === null) === (d.estimate_uom === null), {
    message: 'estimate_value и estimate_uom должны быть оба null или оба заданы',
    path: ['estimate_uom'],
  })
  .refine((d) => d.recurring_rule_id === null || d.recurring_period_key !== null, {
    message: 'Recurring task instance требует recurring_period_key',
    path: ['recurring_period_key'],
  })
  // S21.2 — папка не несёт собственных дат/оценки (сводка считается из детей)
  .refine(
    (d) =>
      d.kind !== 'folder' ||
      (d.start_date === null && d.due_date === null && d.estimate_value === null),
    { message: 'Папка не имеет собственных дат и оценки', path: ['kind'] },
  );
export type Task = z.infer<typeof Task>;

// ─── Move (перенос задачи в другой проект) ───────────────────────────

/**
 * Вход `POST /tasks/:id/move` — перенос задачи (и её поддерева) в другой проект.
 * `target_state_id` опционален: если не задан, backend ремапит статус по группе
 * текущего статуса (fallback — default-статус целевого проекта).
 */
export const TaskMoveInput = z.object({
  target_project_id: UuidString,
  target_state_id: UuidString.optional(),
});
export type TaskMoveInput = z.infer<typeof TaskMoveInput>;

/**
 * Ответ `GET /tasks/:id/move-preview?target_project_id=` — структурный список
 * потерь ДО подтверждения переноса. Только counts/enum/uuid — freeform-текста нет
 * (i18n-инвариант; FE формулирует предупреждение через `@staiter/i18n`).
 */
export const TaskMovePreview = z.object({
  /** Кол-во подзадач в поддереве (без самого корня), которые переедут вместе. */
  subtask_count: z.number().int().nonnegative(),
  /** Кол-во зависимостей задачи, которые будут разорваны (кросс-проектные запрещены). */
  dependency_count: z.number().int().nonnegative(),
  /** Кол-во меток (project-scoped), привязки которых будут сняты. */
  label_count: z.number().int().nonnegative(),
  /** Кол-во заполненных доп. полей, которые будут очищены (чужие field-defs). */
  custom_field_count: z.number().int().nonnegative(),
  /** Ремап статуса: из какой группы, в какой статус целевого проекта (null — не найден). */
  state_remap: z.object({
    from_group: StateGroup,
    to_state_id: UuidString.nullable(),
    /** FE-5 — имена статусов для превью «было → станет» (user-data, не UI-текст). */
    from_state_name: z.string().nullable().optional(),
    to_state_name: z.string().nullable().optional(),
  }),
  /** Номер задачи (PROJ-NNN) сменится всегда — sequence per-project. */
  sequence_will_change: z.literal(true),
  /** В целевом проекте есть хотя бы один статус (иначе перенос заблокирован на UI). */
  target_has_states: z.boolean(),
  /**
   * project-visibility (B21) — видимость источника/цели: FE (F13) предупреждает о
   * рассекречивании (private→open) или засекречивании (open→private) до подтверждения.
   * Optional — forward-compat: старый backend в окне выката поля не шлёт, FE деградирует
   * без предупреждения (безопасно: гейт всё равно на BE).
   */
  source_visibility: ProjectVisibility.optional(),
  target_visibility: ProjectVisibility.optional(),
  /**
   * B1 (task-cross-company-move §2.4) — смена КОМПАНИИ. `null` — перенос внутри
   * одной компании. FE (F4) рисует danger-блок «Задача сменит компанию: A → B»;
   * имена компаний берёт из справочника членств, поэтому здесь только id
   * (freeform-текста в схеме нет — i18n-инвариант).
   *
   * Все поля ниже optional — forward-compat по паттерну visibility-полей выше:
   * старый backend в окне выката их не шлёт, FE не должен на этом падать.
   */
  entity_change: z
    .object({ from_entity_id: UuidString, to_entity_id: UuidString })
    .nullable()
    .optional(),
  /**
   * Ворклоги поддерева с привязкой к производственному заказу. >0 — перенос
   * заблокирован (COGS-денорм исходной компании). До B1 этот гвард стрелял 422
   * уже ПОСЛЕ подтверждения — пользователь узнавал о запрете постфактум.
   */
  wo_worklog_count: z.number().int().nonnegative().optional(),
  /**
   * F8 — что именно мешает переносу. Голого счётчика мало: пользователь знал
   * «нельзя», но не знал, что чистить. Список — до 20 строк (больше в диалоге
   * всё равно не читают, полный разбор — в разделе «Время» задачи).
   *
   * Freeform-текста нет: только id, номер задачи и часы — заголовки строк FE
   * собирает сам через `@staiter/i18n` (i18n-инвариант). Optional —
   * forward-compat, как и остальные поля превью.
   */
  wo_worklogs: z
    .array(
      z.object({
        worklog_id: UuidString,
        task_id: UuidString,
        /** Номер задачи в исходном проекте — по нему её находят глазами. */
        task_sequence_id: z.number().int().nonnegative().nullable(),
        task_title: z.string(),
        /** Часы строкой: Decimal-safe, как остальные количества. */
        duration_hours: z.string(),
        work_order_id: UuidString,
      }),
    )
    .optional(),
  /** §2.5 — ответственных снимет каскад (не члены целевой компании + все группы). */
  assignee_drop_count: z.number().int().nonnegative().optional(),
  /** §2.5 — наблюдателей снимет каскад (не члены целевой компании). */
  watcher_drop_count: z.number().int().nonnegative().optional(),
});
export type TaskMovePreview = z.infer<typeof TaskMovePreview>;

// ─── Cancel subtree (каскадная отмена этапа) ─────────────────────────

/**
 * Строка проекта, у которого нет статуса группы `cancelled` (B7/B8, §3.3 [Б1]).
 *
 * Поимённо, а не счётчиком: пользователю нужно знать, ЧТО именно осталось
 * неотменённым, иначе «пропущено 3» — это приглашение искать их вручную.
 * Названия — user-data (имя проекта, заголовок задачи), не UI-текст: их не
 * локализуют, их показывают.
 */
export const CancelSubtreeSkipped = z.object({
  task_id: UuidString,
  task_title: z.string(),
  project_id: UuidString,
  project_name: z.string(),
});
export type CancelSubtreeSkipped = z.infer<typeof CancelSubtreeSkipped>;

/**
 * Ответ `GET /tasks/:id/cancel-subtree/preview` — что произойдёт с поддеревом
 * ДО подтверждения (SOLUTION §3.3, ред. 3 В-1).
 *
 * Счётчики разнесены по СУДЬБАМ, а не по группам статуса: диалог F13 задаёт ровно
 * один вопрос («отменять ли то, что уже в работе»), и число под этим вопросом
 * обязано быть отдельным от числа незапущенных. Слитый счётчик заставил бы
 * пользователя решать вслепую.
 *
 * Freeform-текста нет (i18n-инвариант §3.9): только числа, uuid и user-data-имена.
 */
export const CancelSubtreePreview = z.object({
  /**
   * Незапущенные дети (`triage` / `backlog` / `unstarted`) — их отменяет ДЕФОЛТ,
   * без дополнительных вопросов.
   */
  to_cancel_not_started: z.number().int().nonnegative(),
  /**
   * Дети в работе и в ожидании (`started` / `paused`) — только по явному
   * `include_in_progress`. Подпись чекбокса покрывает обе группы (В-2).
   */
  in_progress: z.number().int().nonnegative(),
  /** Готовые (`completed`) — не трогаем НИКОГДА: сделанное отменой не отменяют. */
  completed_untouched: z.number().int().nonnegative(),
  /** Уже отменённые — повторная отмена ничего не меняет. */
  already_cancelled: z.number().int().nonnegative(),
  /**
   * Дети в проектах без статуса группы `cancelled` [Б1]: на проде такой проект
   * есть. Дефолтный статус им не подставляется — задача осталась бы «в работе»
   * под видом отменённой.
   */
  skipped_no_cancelled_state: z.array(CancelSubtreeSkipped),
  /**
   * Дети, живущие больше чем в одном проекте: отмена в этапе гасит их и для
   * соседнего проекта, и об этом честно предупреждают заранее.
   */
  multi_project: z.number().int().nonnegative(),
});
export type CancelSubtreePreview = z.infer<typeof CancelSubtreePreview>;

/**
 * Тело `POST /tasks/:id/cancel-subtree`. `include_in_progress` по умолчанию
 * ВЫКЛЮЧЕН (В-1): отмена этапа не должна молча гасить работу, которая уже идёт.
 */
export const CancelSubtreeInput = z
  .object({ include_in_progress: z.boolean().default(false) })
  // zod-strict-ok: request-body — опечатка в единственном поле решает судьбу задач «в работе» (B8).
  .strict();
export type CancelSubtreeInput = z.infer<typeof CancelSubtreeInput>;

/** Ответ мутации: фактические счётчики (не предсказанные) + список отменённых. */
export const CancelSubtreeResult = CancelSubtreePreview.extend({
  cancelled_ids: z.array(UuidString),
});
export type CancelSubtreeResult = z.infer<typeof CancelSubtreeResult>;

// ─── Duplicate (дубль задачи) ────────────────────────────────────────

/**
 * Вход `POST /tasks/:id/duplicate` (пак context-menu, B1 · SOLUTION §1.г3).
 *
 * Клиентская сборка «GET задача → POST create» неатомарна и теряет состав
 * (метки, исполнители, доп. поля, поддерево) — операция серверная.
 *
 * `include_subtree` по умолчанию `false`: пункт меню однокликовый, а копия
 * поддерева — потенциально десятки строк; каскад включается осознанно.
 * Дефолт помечен как «на подтверждение владельцу» (SOLUTION §8).
 *
 * `title` передаёт клиент: суффикс «(копия)» — локализованная UI-строка, и
 * собирать её на сервере значит зашить русский в данные (i18n-инвариант,
 * тот же приём, что у дубля проекта — `m.tmpl_copy_suffix`). Не передан —
 * заголовок копируется как есть.
 */
export const TaskDuplicateInput = z
  .object({
    include_subtree: z.boolean().default(false),
    title: z.string().min(1).max(500).optional(),
  })
  .strict(); // zod-strict-ok:
export type TaskDuplicateInput = z.infer<typeof TaskDuplicateInput>;

/**
 * Ответ `POST /tasks/:id/duplicate`. `subtask_count` — сколько подзадач
 * приехало вместе с копией (0 при `include_subtree:false`); клиент строит по
 * нему тост без второго запроса.
 */
export const TaskDuplicateResult = z
  .object({
    task: Task,
    subtask_count: z.number().int().nonnegative(),
  })
  .strict(); // zod-strict-ok:
export type TaskDuplicateResult = z.infer<typeof TaskDuplicateResult>;

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Извлекает plain-text из TipTap JSON document для description_text / search /
 * email-плейн-режима. Простой walker по `content[]`, склеивает text-узлы.
 *
 * Не использует HTML-parser (TipTap JSON — структурированный AST).
 */
export function stripTipTapText(doc: unknown): string {
  if (!doc || typeof doc !== 'object') return '';
  const parts: string[] = [];
  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    const n = node as Record<string, unknown>;
    if (typeof n.text === 'string') parts.push(n.text);
    // @упоминание — отдельная нода без `text` (attrs.label). Без этого mention
    // терялся в content_text (пустой комментарий в «Активности», поиск, уведомления).
    if (n.type === 'mention') {
      const attrs = n.attrs as Record<string, unknown> | undefined;
      const label = attrs?.label;
      if (typeof label === 'string' && label) parts.push(`@${label}`);
    }
    if (Array.isArray(n.content)) for (const child of n.content) walk(child);
  };
  walk(doc);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}
